import { Suspense, lazy } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { HubLayout } from '@/components/layout/HubLayout';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { ROUTES } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { OrganizationAgentModelsPage } from '@/pages/org/agent-models/OrganizationAgentModelsPage';
import {
  AGENTS_HUB_SECTIONS,
  DEFAULT_AGENTS_HUB_SECTION,
  agentsHubSection,
} from './agents-hub-config';

const AgentPersonasPage = lazy(() =>
  import('@reflex/plugin-agent-persona/web/AgentPersonasPage').then((mod) => ({
    default: mod.AgentPersonasPage,
  })),
);

function AgentsHubContent({ section }: { section: string }) {
  if (section === 'models') {
    return <OrganizationAgentModelsPage embedded />;
  }

  return (
    <Suspense
      fallback={
        <AsyncContent isLoading isError={false} loadingMessage="Loading personas…">
          {null}
        </AsyncContent>
      }
    >
      <AgentPersonasPage embedded />
    </Suspense>
  );
}

/**
 * Host-owned Agents hub: Personas and Agent Models in one left-rail layout.
 * Persona detail / create / edit stay on full-page plugin routes with a back
 * link to the Personas list.
 */
export function AgentsHubPage() {
  const slug = useActiveOrgSlug();
  const { section: sectionParam } = useParams();

  if (!slug) {
    return <Navigate to={ROUTES.settings} replace />;
  }

  const orgRoutes = ROUTES.org(slug);

  if (sectionParam !== 'personas' && sectionParam !== 'models') {
    return <Navigate to={orgRoutes.agentsHubSection(DEFAULT_AGENTS_HUB_SECTION)} replace />;
  }

  const section = sectionParam;
  const activeSection = agentsHubSection(section);

  return (
    <HubLayout
      title="Agents"
      description="Personas and agent model policy for this organization."
      sections={AGENTS_HUB_SECTIONS}
      activeSectionId={section}
      sectionHref={(item) => orgRoutes.agentsHubSection(item.id)}
      navLabel="Agents sections"
    >
      <HubReadGuard permission={activeSection?.permission}>
        <AgentsHubContent section={section} />
      </HubReadGuard>
    </HubLayout>
  );
}
